import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'

interface AnchoredPopoverProps {
  /** Elemento que abriu o painel — o painel se posiciona a partir dele. */
  anchorRef: RefObject<HTMLElement | null>
  onClose: () => void
  /** Largura do painel em px. */
  width?: number
  /** Alinha a borda direita do painel à do gatilho. */
  alignRight?: boolean
  children: ReactNode
}

const GAP = 4
const MARGIN = 8

/**
 * Painel flutuante ancorado num gatilho, desenhado em `document.body` (portal)
 * — dentro de tabela/cartão com `overflow`, um painel absoluto seria cortado.
 * Abre para cima quando não cabe embaixo. A camada de fundo fecha ao clicar
 * fora e SEGURA o clique: sem ela, ele "vazaria" para a linha clicável atrás.
 */
export function AnchoredPopover({
  anchorRef,
  onClose,
  width = 288,
  alignRight = false,
  children,
}: AnchoredPopoverProps) {
  const [pos, setPos] = useState<{
    left: number
    top?: number
    bottom?: number
    maxHeight: number
  } | null>(null)

  const measure = useCallback(() => {
    const r = anchorRef.current?.getBoundingClientRect()
    if (!r) return
    const below = window.innerHeight - r.bottom - MARGIN
    const above = r.top - MARGIN
    const up = below < 280 && above > below
    const rawLeft = alignRight ? r.right - width : r.left
    setPos({
      left: Math.max(MARGIN, Math.min(rawLeft, window.innerWidth - width - MARGIN)),
      ...(up ? { bottom: window.innerHeight - r.top + GAP } : { top: r.bottom + GAP }),
      maxHeight: Math.min(420, up ? above : below),
    })
  }, [anchorRef, alignRight, width])

  // Esc fecha com o foco em qualquer lugar (ex.: o item clicado sumiu da
  // lista e o foco caiu no body). Ao fechar, o foco volta para o gatilho se
  // tinha se perdido — teclado não fica "no vazio".
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  useEffect(() => {
    const anchor = anchorRef.current
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      requestAnimationFrame(() => {
        if (document.activeElement === document.body) anchor?.focus({ preventScroll: true })
      })
    }
  }, [anchorRef])

  useLayoutEffect(() => {
    measure()
    window.addEventListener('scroll', measure, true)
    window.addEventListener('resize', measure)
    return () => {
      window.removeEventListener('scroll', measure, true)
      window.removeEventListener('resize', measure)
    }
  }, [measure])

  return createPortal(
    <>
      <div
        role="presentation"
        // Acima de gavetas/modais (o "Relatar problema" fica em z-9998).
        className="fixed inset-0 z-[10000]"
        onClick={(e) => {
          e.stopPropagation()
          onClose()
        }}
      />
      {pos && (
        <div
          role="dialog"
          onClick={(e) => e.stopPropagation()}
          // Pelo portal, eventos sobem pela árvore React até a linha atrás:
          // teclas digitadas aqui não podem chegar nela (nem ao listener do
          // documento — por isso o Esc também é tratado aqui).
          onKeyDown={(e) => {
            e.stopPropagation()
            if (e.key === 'Escape') onClose()
          }}
          style={{ position: 'fixed', width, ...pos }}
          className="z-[10001] overflow-y-auto rounded-lg border border-border-subtle bg-surface text-left shadow-xl ring-1 ring-black/5"
        >
          {children}
        </div>
      )}
    </>,
    document.body,
  )
}
